import React, { useRef } from 'react';
import { useThumbnailGenerator, useTrimmerDrag } from './hooks';
import { useContainerResize } from '@/hooks';
import {
    getPositionFromTime,
    getTimeFromPosition,
    processDisplayThumbnails,
} from './utils/calculations';
import {
    TrimmerTrack,
    TrimmerHandle,
    TrimmerCaret,
    TrimmerDragArea,
} from './components';

export interface VideoTrimmerProps {
    src: string;
    duration: number;
    trimStart: number;
    trimEnd: number;
    onTrimChange: (start: number, end: number) => void;
    thumbnailCount?: number;
    className?: string;
    cachedThumbnails?: string[];
    onThumbnailsGenerated?: (thumbnails: string[]) => void;
    currentPlaybackTime?: number;
}

export function VideoTrimmer({
    src,
    duration,
    trimStart,
    trimEnd,
    onTrimChange,
    thumbnailCount = 20,
    className = '',
    cachedThumbnails,
    onThumbnailsGenerated,
    currentPlaybackTime,
}: VideoTrimmerProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const actualThumbnailCount = 10;

    const thumbnails = useThumbnailGenerator({
        src,
        duration,
        thumbnailCount: actualThumbnailCount,
        cachedThumbnails,
        onThumbnailsGenerated,
    });

    const containerWidth = useContainerResize(containerRef);

    const getPositionFromTimeLocal = (time: number) =>
        getPositionFromTime(time, duration, containerWidth);
    const getTimeFromPositionLocal = (position: number) =>
        getTimeFromPosition(position, duration, containerWidth);

    const { handleLeftMouseDown, handleRightMouseDown, handleMiddleMouseDown } =
        useTrimmerDrag({
            containerRef,
            containerWidth,
            duration,
            trimStart,
            trimEnd,
            getTimeFromPosition: getTimeFromPositionLocal,
            onTrimChange,
        });

    const leftPosition = getPositionFromTimeLocal(trimStart);
    const rightPosition = getPositionFromTimeLocal(trimEnd);
    const caretPosition =
        currentPlaybackTime !== undefined
            ? getPositionFromTimeLocal(currentPlaybackTime)
            : null;

    const displayThumbnails = processDisplayThumbnails(
        thumbnails,
        thumbnailCount,
    );

    return (
        <div className={`relative ${className}`}>
            <TrimmerTrack
                containerRef={containerRef}
                thumbnails={displayThumbnails}
                displayCount={thumbnailCount}
                leftPosition={leftPosition}
                rightPosition={rightPosition}
                containerWidth={containerWidth}
            />
            <TrimmerDragArea
                leftPosition={leftPosition}
                rightPosition={rightPosition}
                onMouseDown={handleMiddleMouseDown}
            />
            <TrimmerCaret position={caretPosition} />
            <TrimmerHandle
                side="left"
                position={leftPosition}
                onMouseDown={handleLeftMouseDown}
            />
            <TrimmerHandle
                side="right"
                position={rightPosition}
                onMouseDown={handleRightMouseDown}
            />
        </div>
    );
}
